import { KEY } from '../../stores/keys'
import { getLong, logicalKeys, type Storage } from '../../platform/storage'
import { loadProfile } from '../../stores/profile'
import { loadWindows } from '../../stores/history'
import { loadAllNotes, type NoteTag } from '../../stores/notes'
import { loadEvents, windowEnds, type EventKind } from '../../stores/events'
import type { Phase } from '../../core/types'
import { currentKid } from '../../stores/children'

/**
 * Посылка: что устройство рассказывает о себе серверу.
 *
 * Обмен идёт целиком и не боится повторов: у ребёнка есть постоянный номер, у заметок —
 * свои, у окна — момент начала. Побеждает та сторона, где запись свежее.
 */

export const CHILD_ID_KEY = 'moonwake.child_id'

/**
 * Номер малыша для обмена. Это тот же номер, под которым его данные лежат на устройстве,
 * поэтому сервер и устройство говорят об одном и том же ребёнке.
 * Заводится один раз и больше не меняется.
 */
export function childClientId(): string {
  const chosen = currentKid.value
  if (chosen !== null) return chosen
  try {
    const known = globalThis.localStorage?.getItem(CHILD_ID_KEY)
    if (known !== null && known !== undefined && known !== '') return known
    const fresh = `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
    globalThis.localStorage?.setItem(CHILD_ID_KEY, fresh)
    return fresh
  } catch {
    return 'c-local'
  }
}

export interface ChildPayload {
  clientId: string
  /** Анкету завели кнопкой «Добавить второго малыша»: это точно другой ребёнок. */
  separate?: boolean
  /** Постоянный номер малыша, выданный сервером: после первого обмена устройство зовёт его так. */
  serverId?: string | null
  name: string
  birthMs: number
  gender: 'BOY' | 'GIRL'
  gasVariant: boolean
  birthWeightGrams?: number | null
  customPhases: Record<string, Phase[]>
  updatedAtMs: number
}

export interface WindowPayload {
  startedAtMs: number
  endedAtMs: number | null
  programId: string | null
}

export interface NotePayload {
  clientId: number
  day: number
  atMs: number
  tag: NoteTag
  text: string
  deleted: boolean
  updatedAtMs: number
}

export interface EventPayload {
  clientId: number
  kind: EventKind
  atMs: number
  windowMs: number
  phaseIndex: number | null
  title: string | null
  plannedMinutes: number | null
  /** Чем кончилось окно: уснул или сбросили. */
  reason: string | null
  grams: number | null
  /** Окно бодрствования вписали руками, а не отсчитал таймер. */
  manual?: boolean
  deleted?: boolean
}

export interface SyncAnswer {
  child: ChildPayload | null
  /** Под каким номером устройство прислало этого малыша: сервер мог назвать запись иначе. */
  requestedClientId?: string
  /** Показывали ли человеку тур: признак аккаунта, приходит с любым ответом обмена. */
  tourSeen?: boolean
  /** Язык аккаунта и когда его выбрали; `lang: null` — человек ещё не выбирал. */
  locale?: { lang: string | null; updatedAtMs: number }
  /** У аккаунта нет согласия под текущей редакцией политики — показать «догоняющий» экран. */
  needsConsent?: boolean
  windows: WindowPayload[]
  notes: NotePayload[]
  events: EventPayload[]
  serverNow: number
}

/** Все правки «Моего дня», как они лежат в хранилище: ключ — id программы. */
async function collectCustomPhases(storage: Storage): Promise<Record<string, Phase[]>> {
  const out: Record<string, Phase[]> = {}
  for (const key of logicalKeys(await storage.keys())) {
    if (!key.startsWith('custom_phases_')) continue
    const raw = await getLong(storage, key)
    if (raw === null) continue
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) out[key.slice('custom_phases_'.length)] = parsed as Phase[]
    } catch {
      // повреждённая запись не должна мешать обмену
    }
  }
  return out
}

/** Что есть на устройстве прямо сейчас. */
export async function localSnapshot(storage: Storage, known?: string): Promise<{
  child: ChildPayload
  windows: WindowPayload[]
  notes: NotePayload[]
  events: EventPayload[]
} | null> {
  const profile = await loadProfile(storage)
  // Номер спрашиваем только когда есть о ком говорить: иначе `childClientId()` заводил
  // и запоминал номер на устройстве, где малыша нет, и этот номер потом всплывал
  // как «тот самый» при переезде со старой раскладки (случай 12.10.2026)
  if (profile === null) return null
  const clientId = known ?? childClientId()

  const updatedAtMs = Number((await storage.get(KEY.profileUpdatedAt)) ?? 0)
  const events = await loadEvents(storage)
  // Конец окна лежит записью, а не в списке начал: формат `windows_<месяц>` читают
  // часы и виджет Android, менять его нельзя.
  const ends = windowEnds(events)
  return {
    child: {
      clientId,
      separate: (await storage.get(KEY.separateChild)) !== null,
      name: profile.name,
      birthMs: profile.birthMillis,
      gender: profile.gender,
      gasVariant: profile.gasVariant,
      birthWeightGrams: profile.birthWeightGrams ?? null,
      customPhases: await collectCustomPhases(storage),
      updatedAtMs,
    },
    windows: (await loadWindows(storage)).map((startedAtMs) => ({
      startedAtMs,
      endedAtMs: ends.get(startedAtMs) ?? null,
      programId: null,
    })),
    events: events.map((e) => ({
      clientId: e.id,
      kind: e.kind,
      atMs: e.at,
      windowMs: e.window,
      phaseIndex: e.index ?? null,
      title: e.title ?? null,
      plannedMinutes: e.planned ?? null,
      reason: e.reason ?? null,
      grams: e.grams ?? null,
      manual: e.manual === true,
      deleted: e.deleted === true,
    })),
    notes: (await loadAllNotes(storage)).map((note) => ({
      clientId: note.id,
      day: note.day,
      atMs: note.time,
      tag: note.tag,
      text: note.text,
      deleted: note.deleted === true,
      // Сервер сравнивает по этой метке: без неё правка текста выглядела как «ничего нового»
      updatedAtMs: note.updatedAt ?? note.time,
    })),
  }
}
